import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth, roleLabel, can } from "@/lib/auth";
import NotificationBell from "@/components/NotificationBell";
import EnvBanner from "@/components/EnvBanner";
import { API } from "@/lib/api";
import {
  House, Wrench, ClipboardText, CheckSquare, Calendar, Receipt, Toolbox,
  Storefront, UsersThree, Buildings, ChartBar, SignOut, User as UserIcon, Tag, Sparkle, Certificate, ShieldCheck, ClockClockwise, CaretDown, Gear, Warning, EnvelopeSimple, ListChecks,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, tid: "nav-dashboard", section: "dashboard" },
  { to: "/jobs", label: "Jobs", icon: Wrench, tid: "nav-jobs", section: "jobs" },
  { to: "/approvals", label: "Approvals", icon: CheckSquare, tid: "nav-approvals", section: "approvals", roles: ["admin", "general_manager", "district_manager", "area_manager", "maintenance_manager", "store_manager"] },
  { to: "/scheduled", label: "Calendar", icon: Calendar, tid: "nav-scheduled", section: "calendar" },
  { to: "/tasks", label: "Tasks", icon: ListChecks, tid: "nav-tasks", section: "tasks" },
  { to: "/invoices", label: "Invoices", icon: Receipt, tid: "nav-invoices", section: "invoices" },
  { to: "/repair-items", label: "Building repairs", icon: Toolbox, tid: "nav-repair-items", section: "repair_items" },
  { to: "/equipment", label: "Equipment", icon: Wrench, tid: "nav-equipment", section: "equipment" },
  { to: "/maintenance-companies", label: "Maintenance Cos", icon: Buildings, tid: "nav-maintenance-companies", section: "maintenance_companies", roles: ["admin", "general_manager", "maintenance_manager"] },
  { to: "/compliance", label: "Compliance", icon: Certificate, tid: "nav-compliance", section: "compliance" },
  { to: "/stores", label: "Stores", icon: Storefront, tid: "nav-stores", section: "stores" },
  { to: "/brands", label: "Brands", icon: Tag, tid: "nav-brands", section: "brands", roles: ["admin", "general_manager", "maintenance_manager"] },
  { to: "/companies", label: "Companies", icon: Buildings, tid: "nav-companies", section: "companies", roles: ["admin", "general_manager", "maintenance_manager"] },
  { to: "/technicians", label: "Technicians", icon: UsersThree, tid: "nav-technicians", section: "technicians" },
  { to: "/skills", label: "Skills", icon: Sparkle, tid: "nav-skills", section: "skills", roles: ["admin", "general_manager", "maintenance_manager"] },
  { to: "/users", label: "Users & Limits", icon: UsersThree, tid: "nav-users", section: "users", roles: ["admin", "general_manager", "maintenance_manager"] },
  { to: "/invites", label: "Invites", icon: EnvelopeSimple, tid: "nav-invites", section: "invites", roles: ["admin", "general_manager", "maintenance_manager"] },
  { to: "/roles", label: "Roles", icon: ShieldCheck, tid: "nav-roles", section: "roles", roles: ["admin", "general_manager", "maintenance_manager"] },
  { to: "/audit-log", label: "Audit log", icon: ClockClockwise, tid: "nav-audit-log", section: "audit_log", roles: ["admin", "general_manager"] },
  { to: "/reports", label: "Reports", icon: ChartBar, tid: "nav-reports", section: "reports", roles: ["admin", "general_manager", "district_manager", "area_manager", "maintenance_manager", "store_manager"] },
];

function navVisible(item, user) {
  // custom role → permission matrix.
  // built-in role → legacy role-array, UNLESS an admin has edited that role
  // (override present) in which case honor the edited permission matrix.
  if (!user) return false;
  if (user.effective?.is_builtin === false) {
    return can(user, item.section, "read");
  }
  if (user.effective?.has_override) {
    return can(user, item.section, "read");
  }
  return !item.roles || item.roles.includes(user.role);
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <EnvBanner />
      <div className="flex flex-1 min-h-0">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-slate-900 flex flex-col" data-testid="sidebar">
        <div className="px-4 py-5 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md flex items-center justify-center shrink-0" style={{ background: "#002FA7" }}>
              <ClipboardText size={18} weight="bold" color="#fff" />
            </div>
            <div>
              <div className="font-heading font-bold tracking-tight text-[15px] text-white">ServinPri</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Ops Console</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {NAV.filter((n) => navVisible(n, user)).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-[#002FA7] text-white font-medium shadow-sm"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`
              }>
              <n.icon size={16} weight="regular" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-800 p-3">
          <Link to="/profile" data-testid="sidebar-profile-link" className="flex items-center gap-2.5 hover:bg-slate-800 rounded-md p-1.5 transition-colors">
            <div className="h-8 w-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden shrink-0">
              {user?.avatar_file_id ? (
                <img src={`${API}/files/${user.avatar_file_id}`} alt="" className="h-full w-full object-cover" />
              ) : (
                <UserIcon size={14} className="text-slate-400" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate text-white" data-testid="current-user-name">{user?.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{roleLabel(user?.role, user)}</div>
            </div>
          </Link>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-auto">
        <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 px-6 h-14 flex items-center justify-end gap-3" data-testid="topbar">
          <NotificationBell />
          <AvatarMenu user={user} onLogout={async () => { await logout(); navigate("/login"); }} />
        </div>
        {children}
      </main>
      </div>
    </div>
  );
}

function AvatarMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="topbar-avatar-button"
        className="flex items-center gap-1.5 h-9 pl-1 pr-2 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
      >
        <span className="h-7 w-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
          {user?.avatar_file_id ? (
            <img src={`${API}/files/${user.avatar_file_id}`} alt="" className="h-full w-full object-cover" />
          ) : (
            <UserIcon size={12} />
          )}
        </span>
        <CaretDown size={10} weight="bold" className="text-slate-500" />
      </button>
      {open && (
        <div className="absolute right-0 top-11 w-56 bg-white border border-slate-200 rounded-md shadow-lg z-50" data-testid="topbar-avatar-menu">
          <div className="px-3 py-2.5 border-b border-slate-100">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
          </div>
          <Link to="/profile" data-testid="topbar-profile-link" onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50">
            <Gear size={13} /> My profile
          </Link>
          {user?.role === "admin" && (
            <Link to="/admin/danger" data-testid="topbar-admin-danger-link" onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-rose-50 text-rose-700 border-t border-slate-100">
              <Warning size={13} weight="fill" /> Danger zone
            </Link>
          )}
          <button onClick={onLogout} data-testid="topbar-logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-slate-700 border-t border-slate-100">
            <SignOut size={13} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
